/**
 * 지점 허브(오토바이·캠핑장) 랜딩의 SEO 값 — 순수 함수.
 *
 * 모빌리티 허브 요청은 상품 목록 조회를 건너뛰고 데이터셋(StationHubDataset)만 내려보내서
 * (search-data.ts), 상품 결과로 만들던 ItemList JSON-LD 가 비어 있었다. 여기서 데이터셋의
 * 지점 → 상품 인덱스로 같은 목록을 만든다.
 *
 * 범위 판정은 화면·커버리지와 같은 값을 쓴다:
 *  - 지도 영역 검색(MAP_AREA) = 그 범위 안 지점(station-bounds.boundHubStations 와 같은 isInBounds)
 *  - 도시 범위 = 도시 소속 지점(product-backed-cities.branchInCity: 행정구역 ∪ 반경) — 도시 랜딩
 *    목록·city-category-coverage 가 상품 수를 세는 것과 같은 판정(10-19). 화면은 첫 진입에서 전
 *    지점을 가까운 순으로 보여 주므로, 도시 중심에서 가까운 순으로 정렬해 목록 머리와 같게 둔다.
 *  - 지명(district) 범위 = 도시 기본 bounds(getCityBounds) 안 지점
 *  - 지역(admin1) 범위 = 지점 admin1 코드 일치(branch_info.geo_admin1_code)
 *  - 국가 범위 = 지점 country(ISO 코드) 일치
 *  - 그 외 = 전 지점(화면과 같음)
 * 좌표 없는 지점은 화면이 그리지 않으므로(buildStationHubDataFromDataset) 여기서도 뺀다.
 */
import type { HubStation, StationHubDataset } from "@/types/mobility";
import { isInBounds, mapAreaBoundsOf } from "@/lib/mobility/station-bounds";
import { resolveScope, type SearchScope } from "@/lib/search/place-slug";
import { expandAssetUrl } from "@/lib/mobility/station-data";
import { modelKeyOf } from "@/lib/mobility/bike-classes";
import { calculateDistance } from "@/lib/api/products/geo";
import { branchInCity } from "@/lib/seo/product-backed-cities";
import type { City } from "@/config/locations";
import type { Locale } from "@/i18n/routing";

export interface HubBounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

export type HubLandingScope =
  | { kind: "bounds"; bounds: HubBounds; center?: { lat: number; lng: number } | null }
  | { kind: "city"; city: City; center: { lat: number; lng: number } | null }
  | { kind: "region"; code: string }
  | { kind: "country"; code: string }
  | { kind: "all" };

type LocatedStation = HubStation & { lat: number; lng: number };

type ParamSource = { get(key: string): string | null };
type ScopeLocale = Locale;

/**
 * 주소(경로 slug + 검색 파라미터) → 허브 화면이 보는 지점 범위. 서버(제목 보조 줄·메타 설명·
 * ItemList)와 클라이언트(허브 목록·핀)가 **같은 함수**로 판정한다(10-15 — 도시 랜딩 H1 은
 * 도쿄 58곳인데 목록은 전국 243곳이던 어긋남). 지도 영역 검색이면 그 범위, 도시면
 * 도시 소속 지점(+ 가까운 순 기준점), 지명이면 도시 기본 bounds, 지역이면 admin1 코드, 국가면
 * 국가 코드, 그 밖엔 전 지점.
 */
export function hubScopeFromParams(
  region: string,
  locale: string,
  params: ParamSource,
): HubLandingScope {
  const mapArea = mapAreaBoundsOf(params);
  if (mapArea) return { kind: "bounds", bounds: mapArea };

  const scope = (params.get("scope") || undefined) as SearchScope | undefined;
  if (!scope) return { kind: "all" };
  const regionCode = params.get("region");
  if (scope === "region" && regionCode) {
    return { kind: "region", code: regionCode.toUpperCase() };
  }
  let slug = region;
  try {
    slug = decodeURIComponent(region);
  } catch {
    // 잘못된 % 시퀀스 — 원문 그대로 찾는다
  }
  const resolved = resolveScope(
    scope,
    slug,
    params.get("country") ?? undefined,
    locale as ScopeLocale,
  );
  if (scope === "city" && resolved.city) {
    const coords = resolved.city.coordinates;
    return {
      kind: "city",
      city: resolved.city,
      center: coords ? { lat: coords.latitude, lng: coords.longitude } : null,
    };
  }
  if (scope === "district" && resolved.bounds) {
    const coords = resolved.city?.coordinates;
    return {
      kind: "bounds",
      bounds: resolved.bounds,
      center: coords ? { lat: coords.latitude, lng: coords.longitude } : null,
    };
  }
  if (scope === "country" && resolved.country) {
    return { kind: "country", code: resolved.country.code };
  }
  return { kind: "all" };
}

/**
 * 허브 목록을 좁힐 지점 id(클라이언트 boundHubStations 의 범위 집합). 전 지점이면 null.
 * 지도 영역 검색은 boundHubStations 가 범위로 직접 거르므로 여기서도 같은 집합이 나온다.
 * 도시·지명 범위에 지점이 하나도 없으면 null — 빈 목록 대신 전 지점을 가까운 순으로
 * 보여 준다(제목 보조 줄도 지점 0이면 그리지 않는다). 지역·국가 범위는 빈 목록 그대로.
 */
export function hubScopeStationIds(
  stations: ReadonlyArray<HubStation> | null | undefined,
  scope: HubLandingScope,
): Set<string> | null {
  if (!stations || scope.kind === "all") return null;
  const ids = new Set(stationsInScope(stations, scope).map((s) => s.id));
  if ((scope.kind === "bounds" || scope.kind === "city") && ids.size === 0) return null;
  return ids;
}

/** 범위 안 지점(좌표 있는 것만). 도시 중심이 있으면 가까운 순, 아니면 데이터셋 순서. */
export function hubStationsInScope(
  dataset: StationHubDataset,
  scope: HubLandingScope,
): LocatedStation[] {
  return stationsInScope(dataset.stations, scope);
}

function stationsInScope(
  stations: ReadonlyArray<HubStation>,
  scope: HubLandingScope,
): LocatedStation[] {
  const located = stations.filter(
    (s): s is LocatedStation => s.lat != null && s.lng != null,
  );
  if (scope.kind === "all") return located;
  if (scope.kind === "country") {
    const code = scope.code.toUpperCase();
    return located.filter((s) => (s.country ?? "").toUpperCase() === code);
  }
  if (scope.kind === "region") {
    const code = scope.code.toUpperCase();
    return located.filter((s) => (s.admin1 ?? "").toUpperCase() === code);
  }
  const inside =
    scope.kind === "city"
      ? located.filter((s) =>
          branchInCity(scope.city, {
            latitude: s.lat,
            longitude: s.lng,
            admin1: s.admin1,
            admin2: s.admin2,
          }),
        )
      : located.filter((s) => isInBounds(s, scope.bounds));
  const center = scope.center;
  if (!center) return inside;
  return inside
    .map((station, index) => ({
      station,
      index,
      km: calculateDistance(center.lat, center.lng, station.lat, station.lng),
    }))
    .sort((a, b) => a.km - b.km || a.index - b.index)
    .map((entry) => entry.station);
}

/** 범위 안 지점에 연결된 상품 인덱스(지점 순서대로, 중복 제거). */
function productIndexesInScope(stations: readonly HubStation[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const station of stations) {
    for (const index of station.productIdx) {
      if (seen.has(index)) continue;
      seen.add(index);
      out.push(index);
    }
  }
  return out;
}

export interface HubItemListItem {
  url: string;
  name: string;
  image?: string;
}

/** ItemList JSON-LD 항목 — 범위 안 지점의 상품, 최대 `limit` 개. */
export function hubItemListItems(
  dataset: StationHubDataset,
  scope: HubLandingScope,
  options: { baseUrl: string; locale: string; limit?: number },
): HubItemListItem[] {
  const { baseUrl, locale, limit = 30 } = options;
  const items: HubItemListItem[] = [];
  for (const index of productIndexesInScope(hubStationsInScope(dataset, scope))) {
    if (items.length >= limit) break;
    const product = dataset.products[index];
    if (!product || !product.name) continue;
    items.push({
      url: `${baseUrl}/${locale}/products/${product.id}`,
      name: product.name,
      ...(product.image
        ? { image: expandAssetUrl(product.image, dataset.assetBase ?? "") }
        : {}),
    });
  }
  return items;
}

export interface HubScopeStats {
  /** 상품이 하나라도 있는 범위 안 지점 수 */
  stations: number;
  /** 범위 안 지점에 연결된 상품 수 */
  products: number;
  /** 차종(요금제 접미사를 뗀 이름) 수 — 허브 카탈로그의 모델 수와 같은 키 */
  models: number;
  /** 위 지점 중 7일 모두 24시간 운영(isOpen24h)인 곳 — "24시간 무인" 문구는 전부일 때만 */
  open24h: number;
}

export function hubScopeStats(
  dataset: StationHubDataset,
  scope: HubLandingScope,
): HubScopeStats {
  const stations = hubStationsInScope(dataset, scope).filter(
    (s) => s.productIdx.length > 0,
  );
  const indexes = productIndexesInScope(stations);
  const models = new Set<string>();
  for (const index of indexes) {
    const product = dataset.products[index];
    const key = product ? modelKeyOf(product) : "";
    if (key) models.add(key);
  }
  return {
    stations: stations.length,
    products: indexes.length,
    models: models.size,
    open24h: stations.filter((s) => s.isOpen24h).length,
  };
}
